import { motion } from 'framer-motion';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const TermsPage = () => {
    return (
        <div data-testid="terms-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/30"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Legal
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="terms-hero-title"
                        >
                            Terms of Service
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Last updated: March 2026
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-4xl mx-auto">
                    <div className="prose prose-slate max-w-none">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    By accessing and using the Vanfrank Limited website and services, you accept and agree 
                                    to be bound by these Terms of Service. If you do not agree to these terms, please do 
                                    not use our website or services.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Services</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    Vanfrank Limited provides Network Infrastructure and Information Communications Technology 
                                    (ICT) solutions, including but not limited to network infrastructure buildout, global 
                                    connectivity solutions, audio-visual solutions, building management systems, and managed 
                                    services. All services are subject to separate service agreements.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Use of Website</h2>
                                <p className="text-slate-600 leading-relaxed mb-4">
                                    You agree to use our website only for lawful purposes and in a way that does not:
                                </p>
                                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                                    <li>Infringe on the rights of others</li>
                                    <li>Restrict or inhibit anyone else's use of the website</li>
                                    <li>Violate any applicable laws or regulations</li>
                                    <li>Transmit any harmful, threatening, or offensive content</li>
                                    <li>Attempt to gain unauthorized access to our systems</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Intellectual Property</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    All content on this website, including text, graphics, logos, images, and software, is 
                                    the property of Vanfrank Limited or its content suppliers and is protected by intellectual 
                                    property laws. You may not reproduce, distribute, modify, or create derivative works 
                                    without our prior written consent.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Disclaimer of Warranties</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    Our website and services are provided "as is" without any warranties, express or implied. 
                                    We do not warrant that our website will be uninterrupted, error-free, or free of viruses 
                                    or other harmful components. We disclaim all warranties to the fullest extent permitted by law.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Limitation of Liability</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    To the maximum extent permitted by law, Vanfrank Limited shall not be liable for any 
                                    indirect, incidental, special, consequential, or punitive damages arising out of or 
                                    related to your use of our website or services, even if we have been advised of the 
                                    possibility of such damages.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Indemnification</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    You agree to indemnify, defend, and hold harmless Vanfrank Limited and its officers, 
                                    directors, employees, and agents from and against any claims, liabilities, damages, 
                                    losses, and expenses arising out of your use of our website or violation of these terms.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Governing Law</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    These Terms of Service shall be governed by and construed in accordance with the laws 
                                    of the Federal Republic of Nigeria. Any disputes arising under these terms shall be 
                                    subject to the exclusive jurisdiction of the courts of Nigeria.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Changes to Terms</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    We reserve the right to modify these Terms of Service at any time. Changes will be 
                                    effective immediately upon posting on our website. Your continued use of our website 
                                    after any changes constitutes your acceptance of the new terms.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
                                <p className="text-slate-600 leading-relaxed">
                                    If you have any questions about these Terms of Service, please contact us at:
                                </p>
                                <div className="mt-4 p-6 bg-slate-50 rounded-xl">
                                    <p className="text-slate-700 font-medium">Vanfrank Limited</p>
                                    <p className="text-slate-600">Vanfrank Place | 9, Olaribiro Drive, Off Allen Avenue, Ikeja</p>
                                    <p className="text-slate-600">Lagos State, Nigeria</p>
                                    <p className="text-slate-600 mt-2">Email: lagos@vanfranklimited.com</p>
                                    <p className="text-slate-600">Phone: +234 201 227 4476</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TermsPage;
